'use client';

import React, { useState } from 'react';
import { Link as LinkIcon, Check } from 'lucide-react';
import { FaXTwitter as Twitter, FaLinkedin as Linkedin } from 'react-icons/fa6';

export default function EngagementBox() {
    const [vote, setVote] = useState<'yes' | 'no' | null>(null);
    const [copied, setCopied] = useState(false);

    const handleVote = (val: 'yes' | 'no') => {
        setVote(val);
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            // fallback: select the URL manually
            const el = document.createElement('textarea');
            el.value = window.location.href;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        }
    };

    const handleShareTwitter = () => {
        const url = encodeURIComponent(window.location.href);
        const text = encodeURIComponent(`Check out this free utility on CorpToolset, no signup required! 🚀`);
        window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'noopener,noreferrer');
    };

    const handleShareLinkedIn = () => {
        const url = encodeURIComponent(window.location.href);
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <section className="google-auto-ads-no-ad" style={{
            marginBottom: 0,
            padding: 'clamp(1.5rem, 5vw, 3rem)',
            background: '#f8fafc',
            borderRadius: '32px',
            textAlign: 'center',
            border: '1px solid #e2e8f0',
            maxWidth: '800px',
            width: '100%'
        }}>
            {vote === null ? (
                <>
                    <h3 style={{ fontSize: 'var(--fs-xl)', fontWeight: 800, marginBottom: '1rem' }}>Was this tool helpful?</h3>
                    <p style={{ color: '#64748b', marginBottom: '2rem', fontSize: 'var(--fs-base)' }}>Your feedback helps us refine our utilities.</p>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '3rem', flexWrap: 'wrap' }}>
                        <button
                            onClick={() => handleVote('yes')}
                            style={{
                                flex: '0 1 140px',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '12px',
                                background: '#fff',
                                border: '1px solid #e2e8f0',
                                fontWeight: 700,
                                fontSize: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={e => {
                                (e.currentTarget as HTMLButtonElement).style.background = '#f0fdf4';
                                (e.currentTarget as HTMLButtonElement).style.borderColor = '#10b981';
                            }}
                            onMouseLeave={e => {
                                (e.currentTarget as HTMLButtonElement).style.background = '#fff';
                                (e.currentTarget as HTMLButtonElement).style.borderColor = '#e2e8f0';
                            }}
                        >
                            <span>👍</span> Yes
                        </button>
                        <button
                            onClick={() => handleVote('no')}
                            style={{
                                flex: '0 1 140px',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '12px',
                                background: '#fff',
                                border: '1px solid #e2e8f0',
                                fontWeight: 700,
                                fontSize: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={e => {
                                (e.currentTarget as HTMLButtonElement).style.background = '#fef2f2';
                                (e.currentTarget as HTMLButtonElement).style.borderColor = '#ef4444';
                            }}
                            onMouseLeave={e => {
                                (e.currentTarget as HTMLButtonElement).style.background = '#fff';
                                (e.currentTarget as HTMLButtonElement).style.borderColor = '#e2e8f0';
                            }}
                        >
                            <span>👎</span> No
                        </button>
                    </div>
                </>
            ) : (
                <div style={{ marginBottom: '3rem' }}>
                    {vote === 'yes' ? (
                        <div style={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: 'clamp(1.5rem, 5vw, 2.5rem)',
                            background: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                            borderRadius: '20px',
                            maxWidth: '100%'
                        }}>
                            <span style={{ fontSize: '2.5rem' }}>🎉</span>
                            <p style={{ fontWeight: 800, fontSize: 'var(--fs-lg)', color: '#065f46', margin: 0 }}>
                                Thank you! We're glad it helped.
                            </p>
                            <p style={{ color: '#047857', margin: 0, fontSize: 'var(--fs-sm)' }}>
                                Share it with your team to speed up their workflow too.
                            </p>
                        </div>
                    ) : (
                        <div style={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: 'clamp(1.5rem, 5vw, 2.5rem)',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '20px',
                            maxWidth: '100%'
                        }}>
                            <span style={{ fontSize: '2.5rem' }}>🛠️</span>
                            <p style={{ fontWeight: 800, fontSize: 'var(--fs-lg)', color: '#991b1b', margin: 0 }}>
                                We appreciate the feedback!
                            </p>
                            <p style={{ color: '#dc2626', margin: 0, fontSize: 'var(--fs-sm)' }}>
                                We'll work on improving it. Contact us at{' '}
                                <a href="mailto:contact@corptoolset.com" style={{ color: 'var(--primary)', fontWeight: 700, textDecoration: 'none' }} className="hover-underline">
                                    contact@corptoolset.com
                                </a>
                            </p>
                        </div>
                    )}
                </div>
            )}

            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '2.5rem' }}>
                <p style={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: 'var(--primary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    marginBottom: '1.5rem'
                }}>
                    Share this utility
                </p>
                <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                        onClick={handleShareTwitter}
                        title="Share on X (Twitter)"
                        style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            background: '#000',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'transform 0.2s, opacity 0.2s',
                            fontSize: '1.1rem',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.8'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                    >
                        <Twitter size={20} fill="currentColor" />
                    </button>
                    <button
                        onClick={handleShareLinkedIn}
                        title="Share on LinkedIn"
                        style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            background: '#0077b5',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'transform 0.2s, opacity 0.2s',
                            fontSize: '1.1rem',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.8'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                    >
                        <Linkedin size={20} fill="currentColor" />
                    </button>
                    <button
                        onClick={handleCopyLink}
                        title={copied ? 'Link Copied!' : 'Copy Link'}
                        style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            background: copied ? '#10b981' : 'var(--primary)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'background 0.3s',
                            fontSize: '1.1rem',
                        }}
                    >
                        {copied ? <Check size={20} /> : <LinkIcon size={20} />}
                    </button>
                </div>
                {copied && (
                    <p style={{ color: '#10b981', fontWeight: 700, fontSize: '0.9rem', marginTop: '1rem' }}>
                        ✅ Link copied to clipboard!
                    </p>
                )}
            </div>
        </section>
    );
}
